import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import * as Styled from "./styled";
import Toggle from "@/components/shared-ui/Toggle";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import useApi from "@/contexts/ApiContext";
import type { MsSlaMetricRow } from "@/services/api/types";
import SlaTable from "./SLATable";
import Page from "@/components/layout/Page";
import Card from "@/components/shared-ui/Card";
import ContactCard from "@/components/shared-ui/ContactCard";
import useCompany from "@/contexts/CompanyContext";
import NotFound from "../NotFound";

const ServiceTargets = () => {
  const api = useApi();
  const { companyStatus } = useCompany();

  const [isLongTerm, setIsLongTerm] = useState(false);

  const { data: slaViews } = useQuery({
    queryKey: ["get-ms-slas"],
    queryFn: () => api.ms.getSlas(),
    placeholderData: keepPreviousData,
  });

  const isOnboarding = companyStatus === "onboarding";

  if (isOnboarding) {
    return <NotFound />;
  }

  if (!slaViews) {
    return <LoadingScreen />;
  }

  // Group metrics by area, with areas sorted alphabetically
  const slaData = isLongTerm ? slaViews.longTerm : slaViews.recent;

  const groupedByArea = slaData.rows.reduce(
    (accumulator, row) => {
      (accumulator[row.area] = accumulator[row.area] || []).push(row);
      return accumulator;
    },
    {} as { [area: string]: MsSlaMetricRow[] },
  );

  const sortedAreas = Object.keys(groupedByArea).sort();

  return (
    <Page title="Service targets">
      <Styled.ServiceTargets>
        <Card>
          <Styled.PageHeading>Service targets</Styled.PageHeading>
          {slaViews.longTerm.reportingPeriods.length > 3 && (
            <Styled.ViewToggle>
              <Toggle
                label="Show long-term view:"
                isChecked={isLongTerm}
                handleToggle={() => setIsLongTerm((prev) => !prev)}
              />
            </Styled.ViewToggle>
          )}
          <Styled.ServiceTargetsContent>
            <h3>Performance against service targets</h3>
            {!slaData.rows.length ? (
              <p>
                No SLA data is visible at this time. Please contact your account
                manager with any concerns.
              </p>
            ) : (
              <Styled.TableWrapper>
                {sortedAreas.map((area) => (
                  <div key={area}>
                    <Styled.AreaRow>
                      <h4>{area}</h4>
                    </Styled.AreaRow>
                    <Styled.AreaTable
                      isSuccessArea={area === "Customer success"}
                    >
                      <SlaTable
                        periods={slaData.reportingPeriods}
                        data={groupedByArea[area]}
                      />
                    </Styled.AreaTable>
                  </div>
                ))}
              </Styled.TableWrapper>
            )}
          </Styled.ServiceTargetsContent>
        </Card>
        <ContactCard />
      </Styled.ServiceTargets>
    </Page>
  );
};

export default ServiceTargets;
